"use client";

import { PortableText } from "next-sanity";
import { richTextComponent } from "@/sanity/sanityPortableText";
import Image from "@/helpers/Image";
import { CTAButton } from "@/shadcn/ui/button";
import { PageAnniversarySchema } from "@/sanity/pages/PageAnniversaryTemplate";

export default function FounderSection({
  data,
}: {
  data: PageAnniversarySchema;
}) {
  if (!data.founderTitle && !data.founderImage) return null;

  return (
    <section className="w-full bg-[#f7f7f7]">
      <div className="mx-auto w-[min(170rem,100%)] px-[3rem] lg:px-0 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-[8rem] lg:gap-[11.625rem] pt-[7rem] lg:pt-[20rem] pb-[7rem] lg:pb-[15rem]">
        {/* Left: text + credentials */}
        <div className="flex flex-col gap-[3rem] lg:gap-[5rem] lg:w-[78.125rem] shrink-0">
          {/* Header */}
          <div className="flex flex-col gap-[1.25rem]">
            {data.founderChip && (
              <p className="mb-0 font-blacker italic text-[2rem] leading-[1.5] text-black">
                {data.founderChip}
              </p>
            )}
            {data.founderTitle && (
              <div className="richtext richtext-start prose-headings:font-medium max-sm:prose-headings:text-[5rem] lg:prose-headings:text-[8rem] max-sm:prose-headings:tracking-[-0.25rem] prose-headings:mb-[2rem] prose-p:max-w-[68rem] prose-p:text-[2.25rem]">
                <PortableText
                  value={data.founderTitle}
                  components={richTextComponent}
                />
              </div>
            )}
          </div>
          {data.founderImage && (
            <div className="lg:hidden w-full lg:w-[75rem] flex-shrink-0">
              <Image src={data.founderImage} alt="" className="w-full" />
            </div>
          )}

          {/* Credentials */}
          {!!data.founderCredentials?.length && (
            <div className="flex flex-col">
              {data.founderCredentials.map((cred, i) => (
                <div key={cred._key ?? i}>
                  {i > 0 && <div className="h-px bg-black/10" />}
                  <div className="flex gap-[4.875rem] items-center py-[2.5rem]">
                    <p className="mb-0 font-spaceMono font-medium text-[#228e71] text-[1.75rem] leading-none tracking-[-0.0525rem] shrink-0 w-[3rem]">
                      {String(i + 1).padStart(2, "0")}
                    </p>
                    {cred.text && (
                      <div className="flex-1 richtext richtext-start font-geist text-[2.25rem] leading-[1.5] text-black/70 [&_strong]:text-black [&_strong]:font-medium">
                        <PortableText
                          value={cred.text}
                          components={richTextComponent}
                        />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          {data.founderCTA && (
            <div className="lg:hidden w-full lg:w-fit">
              <CTAButton data={data.founderCTA} />
            </div>
          )}
        </div>

        {/* Right: portrait image */}
        {data.founderImage && (
          <div className="hidden lg:block w-full lg:w-[75rem] flex-shrink-0">
            <Image src={data.founderImage} alt="" className="w-full" />
          </div>
        )}
      </div>
    </section>
  );
}
